import { API_BASE_URL } from '../Utils/constants';
import { authFetch } from './authFetch';
import { spaceOwnerQueryParams } from './spaceMembersService';
import { INQUIRY_EMAIL_SOURCES } from '../Utils/inquiryEmailSource';
import { normalizeSharedListCommercial } from '../Leads/commercialSummary';

// El Bearer (renovado, sin `Bearer null`, reintento ante 401) lo pone `authFetch`.
const getHeaders = () => ({ 'Content-Type': 'application/json' });

// ─────────────────────────────────────────────────────────────────────────────
// Compartir listas (user_lot_lists)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Marca elegida al compartir. `null` es un valor con significado propio:
 * "seguí la principal", y el enlace la sigue si después cambia cuál es.
 * Por eso viaja explícito en vez de omitirse.
 * Con `share_template: 'default'` el backend la ignora y limpia la guardada.
 *
 * @param {string} shareTemplate
 * @param {string|null|undefined} brandId
 * @returns {{ brand_id?: string|null }}
 */
const brandIdBody = (shareTemplate, brandId) => {
  if (shareTemplate !== 'personalized') return {};
  return { brand_id: brandId ? String(brandId) : null };
};

/**
 * `inquiry_email_source` solo si viene un valor válido (el diálogo del dueño
 * no lo manda; el invitado reenvía el guardado para no pisarlo).
 *
 * @param {string|null|undefined} value
 * @returns {{ inquiry_email_source?: string }}
 */
const inquiryEmailSourceBody = (value) => (
  INQUIRY_EMAIL_SOURCES.includes(value) ? { inquiry_email_source: value } : {}
);

/**
 * JSON de una escritura de share + metadatos HTTP (aditivo: `data` / `error`
 * siguen donde estaban). Un body que no es JSON sigue lanzando, como antes.
 *
 * @param {Response} response
 */
const readShareWriteResponse = async (response) => {
  const json = await response.json();
  return { ...json, _httpStatus: response.status, _httpOk: response.ok };
};

/**
 * POST /shareuserlotlist. En espacio ajeno (F3) mandá `ownerUserId` en el
 * body (`owner_user_id`): el invitado comparte la lista del owner con la marca
 * del owner. Sin `ownerUserId` → igual que siempre (cuenta del JWT).
 * El POST reescribe la config entera: el invitado reenvía la que leyó del GET
 * (incluido `inquiry_email_source`, que el dueño ya no manda).
 * Respuesta: JSON + `_httpStatus` / `_httpOk`.
 */
export const shareUserLotList = async ({
  list_id,
  scope = 'all',
  access_mode,
  allowlist,
  send_invite,
  invite_message,
  invite_emails,
  share_template,
  brand_id,
  show_cadastral_base,
  inquiry_email_source,
  ownerUserId,
}) => {
  const body = {
    list_id,
    scope,
    ...(access_mode ? { access_mode } : {}),
    ...(Array.isArray(allowlist) ? { allowlist } : {}),
    ...(share_template === 'personalized' || share_template === 'default'
      ? { share_template, ...brandIdBody(share_template, brand_id) }
      : {}),
    ...(typeof show_cadastral_base === 'boolean' ? { show_cadastral_base } : {}),
    ...inquiryEmailSourceBody(inquiry_email_source),
    ...spaceOwnerQueryParams(ownerUserId),
  };

  // Invite SES: restricted (ver/editar) o público (solo as_editor).
  // Contrato: send_invite + invite_emails (solo emails nuevos). Sin nuevos → no enviar invite (UI valida).
  const canInvite = access_mode === 'restricted' || access_mode === 'public';
  if (canInvite && send_invite === true) {
    const recipients = Array.isArray(invite_emails)
      ? invite_emails.map((e) => String(e).trim().toLowerCase()).filter(Boolean)
      : [];
    if (recipients.length > 0) {
      body.send_invite = true;
      body.invite_emails = recipients;
      const message = typeof invite_message === 'string' ? invite_message.trim() : '';
      if (message) body.invite_message = message.slice(0, 2000);
    }
  }

  const response = await authFetch(`${API_BASE_URL}/shareuserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return readShareWriteResponse(response);
};

/**
 * POST /unshareuserlotlist. `ownerUserId` → `owner_user_id` en el body (F3).
 * Rota el token y deja `is_shared=false` (200 aunque no estuviera shared).
 * Respuesta: JSON + `_httpStatus` / `_httpOk`.
 */
export const unshareUserLotList = async ({ list_id, ownerUserId }) => {
  const response = await authFetch(`${API_BASE_URL}/unshareuserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      list_id,
      ...spaceOwnerQueryParams(ownerUserId),
    }),
  });
  return readShareWriteResponse(response);
};

/**
 * Config del share de una lista.
 * @param {string} listId
 * @param {{ ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (lista del owner en espacio ajeno;
 *        F1 lectura, F3 al abrir el diálogo de compartir).
 */
export const getUserLotListShare = async (listId, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    list_id: listId,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(
    `${API_BASE_URL}/getuserlotlistshare?${params}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

/** @param {AbortSignal} [signal] */
export const getSharedWithMeLists = async (signal) => {
  const response = await authFetch(`${API_BASE_URL}/getsharedwithmelists`, {
    method: 'GET',
    headers: getHeaders(),
    signal,
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

export const hideSharedUserLotList = async ({ list_id }) => {
  const response = await authFetch(`${API_BASE_URL}/hideshareduserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ list_id }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

export const leaveSharedUserLotList = async ({ list_id }) => {
  const response = await authFetch(`${API_BASE_URL}/leaveshareduserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ list_id }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

export const showSharedUserLotList = async ({ list_id }) => {
  const response = await authFetch(`${API_BASE_URL}/showshareduserlotlist`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ list_id }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

/**
 * @param {string} token
 * @param {{
 *   format?: 'flat' | 'grouped',
 *   includeFilterAttributes?: boolean,
 *   includePrivateAttributes?: boolean,
 * }} [options] `grouped` alinea con getmylots. `includeFilterAttributes` pide los
 *   atributos de parcela y `includePrivateAttributes` los de la publicación
 *   (superficie, ambientes, servicios) para filtrar la lista en cliente.
 * @see docs/maps/parcel-filters-scope-backend-spec.md
 */
export const getSharedList = async (
  token,
  { format, includeFilterAttributes, includePrivateAttributes, includeCommercialSummary = false } = {},
) => {
  // Público con JWT opcional: con sesión `authFetch` manda Bearer; sin sesión, anónimo.
  const params = new URLSearchParams({ token });
  if (format) params.set('format', format);

  const include = [
    includeFilterAttributes ? 'filter_attributes' : null,
    includePrivateAttributes ? 'private_attributes' : null,
    // Solo si el usuario gestiona la lista (API lo ignora sin `viewer_can_manage`). Nunca share / público.
    includeCommercialSummary ? 'commercial_summary' : null,
  ].filter(Boolean);
  if (include.length) params.set('include', include.join(','));

  const response = await authFetch(`${API_BASE_URL}/getsharedlist?${params.toString()}`);

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  if (includeCommercialSummary && json?.data) {
    json = { ...json, data: normalizeSharedListCommercial(json.data) };
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

/**
 * Detalle autorizado por token de lista compartida.
 * @param {string} token
 * @param {{ userLotId?: string|null, lotId?: string|null }} [opts]
 *   Unidad: userLotId. Lote directo del snapshot: lotId (sin user_lot_id).
 */
export const getSharedLotDetail = async (token, opts = {}) => {
  // Público con JWT opcional: con sesión `authFetch` manda Bearer; sin sesión, anónimo.
  const userLotId = typeof opts === 'string' ? opts : opts?.userLotId;
  const lotId = typeof opts === 'string' ? null : opts?.lotId;

  const params = new URLSearchParams({ token });
  if (userLotId) params.set('user_lot_id', String(userLotId));
  else if (lotId) params.set('lot_id', String(lotId));

  const response = await authFetch(
    `${API_BASE_URL}/getsharedlotdetail?${params.toString()}`,
  );

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Compartir un solo user_lot (user_lot_shares)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Genera / actualiza share de un lote (+ unidades opcionales).
 * Preferido: `{ lot_id, user_lot_ids }`.
 * Compat: `{ user_lot_id }` (una sola unidad; el BE infiere lot_id).
 */
/**
 * POST /shareuserlot. `ownerUserId` → `owner_user_id` en el body (F3): el
 * invitado comparte el lote / UFs del owner. Sin `ownerUserId` → JWT.
 * Respuesta: JSON + `_httpStatus` / `_httpOk`.
 */
export const shareUserLot = async ({
  lot_id,
  user_lot_ids,
  user_lot_id,
  scope = 'all',
  access_mode,
  allowlist,
  send_invite,
  invite_emails,
  invite_message,
  rotate_token,
  share_template,
  brand_id,
  inquiry_email_source,
  ownerUserId,
}) => {
  const isRestricted = access_mode === 'restricted';
  const body = {
    scope,
    ...(lot_id ? { lot_id } : {}),
    ...(Array.isArray(user_lot_ids) ? { user_lot_ids } : {}),
    ...(user_lot_id && !lot_id ? { user_lot_id } : {}),
    ...(access_mode ? { access_mode } : {}),
    ...(Array.isArray(allowlist) ? { allowlist } : {}),
    ...(rotate_token === true ? { rotate_token: true } : {}),
    ...(share_template === 'personalized' || share_template === 'default'
      ? { share_template, ...brandIdBody(share_template, brand_id) }
      : {}),
    ...inquiryEmailSourceBody(inquiry_email_source),
    ...spaceOwnerQueryParams(ownerUserId),
  };

  // Invitaciones por email: solo en restricted (public → 400).
  // Contrato igual que listas: send_invite + invite_emails (solo emails nuevos).
  if (isRestricted && send_invite === true) {
    const recipients = Array.isArray(invite_emails)
      ? invite_emails.map((e) => String(e).trim().toLowerCase()).filter(Boolean)
      : [];
    if (recipients.length > 0) {
      body.send_invite = true;
      body.invite_emails = recipients;
      const message = typeof invite_message === 'string' ? invite_message.trim() : '';
      if (message) body.invite_message = message.slice(0, 2000);
    }
  }

  const response = await authFetch(`${API_BASE_URL}/shareuserlot`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return readShareWriteResponse(response);
};

/**
 * POST /unshareuserlot. Preferido: `{ lot_id }`. Compat: `{ user_lot_id }`.
 * `ownerUserId` → `owner_user_id` en el body (F3). Sin activo → 404.
 * Respuesta: JSON + `_httpStatus` / `_httpOk`.
 */
export const unshareUserLot = async ({ lot_id, user_lot_id, ownerUserId }) => {
  const body = {
    ...(lot_id ? { lot_id } : { user_lot_id }),
    ...spaceOwnerQueryParams(ownerUserId),
  };
  const response = await authFetch(`${API_BASE_URL}/unshareuserlot`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return readShareWriteResponse(response);
};

/**
 * Config del share activo.
 * Preferido: `getUserLotShare({ lotId })`.
 * Compat: `getUserLotShare(userLotId)` o `getUserLotShare({ userLotId })`.
 * Opcional `ownerUserId` → `?owner_user_id=` (lote del owner en espacio ajeno;
 * F1 lectura, F3 al abrir el diálogo de compartir).
 *
 * @param {string|{ lotId?: string, lot_id?: string, userLotId?: string, user_lot_id?: string, ownerUserId?: string|null }} lotIdOrOpts
 */
export const getUserLotShare = async (lotIdOrOpts) => {
  const opts = typeof lotIdOrOpts === 'string'
    ? { userLotId: lotIdOrOpts }
    : (lotIdOrOpts || {});
  const lotId = opts.lotId || opts.lot_id;
  const userLotId = opts.userLotId || opts.user_lot_id;

  const params = new URLSearchParams();
  if (lotId) params.set('lot_id', String(lotId));
  else if (userLotId) params.set('user_lot_id', String(userLotId));
  const { owner_user_id: ownerUserId } = spaceOwnerQueryParams(opts.ownerUserId);
  if (ownerUserId) params.set('owner_user_id', ownerUserId);

  const response = await authFetch(
    `${API_BASE_URL}/getuserlotshare?${params.toString()}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

export const getSharedUserLot = async (token) => {
  // Público con JWT opcional: con sesión `authFetch` manda Bearer; sin sesión, anónimo.
  const response = await authFetch(
    `${API_BASE_URL}/getshareduserlot?token=${encodeURIComponent(token)}`,
  );

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

